import { useEffect, useState, useCallback } from 'react';
import {
  adminApi,
  type AdminWorldDetail,
  type AdminWorldState,
  type AdminWorldEvolution,
  type AdminWorldVisualGeneration,
} from '../lib/api';

export interface WorldDataState {
  world: AdminWorldDetail | null;
  state: AdminWorldState | null;
  evolutions: AdminWorldEvolution[];
  visuals: AdminWorldVisualGeneration[];
  versions: Array<{ id: string; version: number; updatedAt: string }>;
  previousState: AdminWorldState | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseWorldDataReturn extends WorldDataState {
  refetchWorld: () => Promise<void>;
  refetchState: () => Promise<void>;
  setStateVersion: (version: number | null) => void;
  currentVersion: number | null;
}

export function useWorldData(worldId: string | undefined, initialVersion?: number | null): UseWorldDataReturn {
  const [world, setWorld] = useState<AdminWorldDetail | null>(null);
  const [state, setState] = useState<AdminWorldState | null>(null);
  const [evolutions, setEvolutions] = useState<AdminWorldEvolution[]>([]);
  const [visuals, setVisuals] = useState<AdminWorldVisualGeneration[]>([]);
  const [versions, setVersions] = useState<Array<{ id: string; version: number; updatedAt: string }>>([]);
  const [previousState, setPreviousState] = useState<AdminWorldState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<number | null>(initialVersion ?? null);

  // Fetch world data
  const fetchWorld = useCallback(async () => {
    if (!worldId) return;
    try {
      const data = await adminApi.getWorld(worldId);
      setWorld(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load world');
    }
  }, [worldId]);

  // Fetch world state
  const fetchState = useCallback(async () => {
    if (!worldId) return;
    try {
      const [stateData, versionsData] = await Promise.all([
        currentVersion
          ? adminApi.getWorldStateVersion(worldId, currentVersion)
          : adminApi.getWorldState(worldId),
        adminApi.getWorldStateVersions(worldId, 50),
      ]);
      setState(stateData);
      setVersions(versionsData.versions);
    } catch (err) {
      // World state may not exist yet
      setState(null);
      setVersions([]);
    }
  }, [worldId, currentVersion]);

  // Fetch evolutions
  const fetchEvolutions = useCallback(async () => {
    if (!worldId) return;
    try {
      const data = await adminApi.getWorldEvolutions(worldId, 20);
      setEvolutions(data.evolutions);
    } catch {
      setEvolutions([]);
    }
  }, [worldId]);

  // Fetch world visuals
  const fetchVisuals = useCallback(async () => {
    if (!worldId) return;
    try {
      const data = await adminApi.getWorldVisuals(worldId, 50);
      setVisuals(data.visuals ?? []);
    } catch {
      setVisuals([]);
    }
  }, [worldId]);

  // Fetch previous state for comparison
  useEffect(() => {
    let active = true;
    const fetchPrevious = async () => {
      if (!worldId || !state || versions.length === 0) {
        setPreviousState(null);
        return;
      }
      const sorted = [...versions].sort((a, b) => b.version - a.version);
      const currentIndex = sorted.findIndex((v) => v.version === state.version);
      const previous = currentIndex >= 0 ? sorted[currentIndex + 1] : null;
      if (!previous) {
        setPreviousState(null);
        return;
      }
      try {
        const previousStateData = await adminApi.getWorldStateVersion(worldId, previous.version);
        if (active) {
          setPreviousState(previousStateData);
        }
      } catch {
        if (active) {
          setPreviousState(null);
        }
      }
    };

    fetchPrevious();
    return () => { active = false; };
  }, [worldId, state, versions]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      if (!worldId) return;
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchWorld(), fetchState(), fetchEvolutions(), fetchVisuals()]);
      setIsLoading(false);
    };
    loadData();
  }, [worldId, fetchWorld, fetchState, fetchEvolutions, fetchVisuals]);

  const setStateVersion = useCallback((version: number | null) => {
    setCurrentVersion(version);
  }, []);

  const refetchWorld = useCallback(async () => {
    await fetchWorld();
  }, [fetchWorld]);

  const refetchState = useCallback(async () => {
    await fetchState();
  }, [fetchState]);

  return {
    world,
    state,
    evolutions,
    visuals,
    versions,
    previousState,
    isLoading,
    error,
    refetchWorld,
    refetchState,
    setStateVersion,
    currentVersion,
  };
}
